import React, { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { useAccessToken, useCheckBox, useViewport } from '@chilecompra/react-kit'
import { CheckBox, Tag, Tooltip, Typography } from '@chilecompra/react-kit/components'
import {
  EXPIRES_AT,
  PURCHASE_ORDER_STATUS_DICTIONARY,
  QUOTE_BID_STATUS_DICTIONARY,
  QUOTE_STATUSES_DICTIONARY,
  TYPE_OFFER_DICTIONARY
} from '../../config/settings/constants'
import { REACT_APP_PROVIDERS_BASE_URL } from '../../config/settings/environment'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import { formatTypeCurrency } from '../../modules/utils/formatTypeCurrency'
import {
  Card,
  Divider,
  ExclamationIcon,
  GridContainer,
  GridItem,
  Label,
  LetterBlue,
  LetterAbilityAffidavit,
  LetterDeclarationInadmissibility,
  LinkLabel,
  Strong,
  TooltipText,
  WorkIcon
} from './QuoteBid.styles'
import ProtectByFeatures from '../../components/Security/ProtectByFeatures'

/**
 * The Quote Bidding's component.
 */
const QuoteBidding = props => {
  const {
    active,
    amount,
    bidId,
    businessName,
    companySize,
    description,
    disableOtherOffer,
    disableOtherOfferTotal,
    dni,
    inadmissibility,
    isFailedSelected,
    isSelectedBid,
    loading,
    onAbilityAffidavit,
    onDeclarationInadmissibility,
    onSeeDetail,
    openPurchaseOrderStatus,
    purchaseOrderStatus,
    regions,
    selectedCheckbox,
    selectedOffers,
    statusBidId,
    statusId,
    typeCurrency,
    typeOffer,
    validityDate
  } = props

  const { authInfo } = useSelector(state => state.authorizationProvider)
  const { isBuyer, isSeller } = useAuthorization()
  const { size } = useViewport()
  const accessToken = useAccessToken()

  const checkValidation = statusId === QUOTE_STATUSES_DICTIONARY.CLOSED
  const checkInadmissibility = statusBidId === QUOTE_BID_STATUS_DICTIONARY.INADMISSIBILITY
  const {
    value: checked,
    onChange: handleCheckBox,
    setValue: setCheckBoxValue
  } = useCheckBox({
    initialValues: true,
    changeCallback: updatedValue => {
      if (selectedOffers) {
        selectedCheckbox({
          valueCheck: false,
          offerId: bidId,
          typeOfferSelected: typeOffer,
          totalAmount: amount,
          checked,
          byInadmissibility: true
        })
      } else {
        selectedCheckbox({
          valueCheck: updatedValue,
          offerId: bidId,
          typeOfferSelected: typeOffer,
          totalAmount: amount,
          checked
        })
      }
    }
  })

  const disabledOffer = () => {
    if (selectedOffers) {
      return false
    }
    /**
     * blocking if it comes from the backend in unselectable mode
     */
    if (!isSelectedBid || authInfo.hasObserverRole) {
      return true
    }
    /**
     * block when inadmissible
     */
    if (statusBidId && statusBidId === QUOTE_BID_STATUS_DICTIONARY.INADMISSIBILITY) {
      return true
    }
    /**
     * when there is a selected
     */
    if (disableOtherOffer) {
      /**
       * Locks if there is a total mode offer selected or the offer type is equal to TOTAL
       */
      return disableOtherOfferTotal || typeOffer === TYPE_OFFER_DICTIONARY.TOTAL
    }

    return false
  }
  const checkStatus = useCallback(
    statusCode =>
      statusCode === PURCHASE_ORDER_STATUS_DICTIONARY.CANCELLED_BUYER ||
      statusCode === PURCHASE_ORDER_STATUS_DICTIONARY.CANCELLED ||
      statusCode === PURCHASE_ORDER_STATUS_DICTIONARY.NOT_ACCEPTED,
    []
  )
  const checkPurchaseOrder = useCallback(
    idState =>
      (isBuyer && idState !== PURCHASE_ORDER_STATUS_DICTIONARY.SAVED) ||
      (isSeller &&
        (idState === PURCHASE_ORDER_STATUS_DICTIONARY.ACCEPTED ||
          idState === PURCHASE_ORDER_STATUS_DICTIONARY.CANCELLED_BUYER ||
          idState === PURCHASE_ORDER_STATUS_DICTIONARY.CANCELLED ||
          idState !== PURCHASE_ORDER_STATUS_DICTIONARY.SAVED ||
          idState === PURCHASE_ORDER_STATUS_DICTIONARY.REMOVED)),
    [isBuyer, isSeller]
  )
  const getPurchaseOrderStatusText = useCallback(status => {
    if (!checkPurchaseOrder(status)) {
      return ''
    }

    return checkStatus(status)
      ? ' cancelada '
      : status === PURCHASE_ORDER_STATUS_DICTIONARY.REMOVED
        ? ' eliminada'
        : ' emitida '
  }, [])

  const providerFileUrl = useMemo(
    () =>
      isBuyer
        ? `${REACT_APP_PROVIDERS_BASE_URL}/ficha/callback?access_token=${accessToken}&expires_in=${EXPIRES_AT}&rut=${dni}`
        : `${REACT_APP_PROVIDERS_BASE_URL}/ficha/${dni}`,
    [isBuyer, dni]
  )

  const handleSeeDetail = () => onSeeDetail(bidId)
  const handleAbilityAffidavit = () => onAbilityAffidavit(bidId)
  const handleDeclarationInadmissibility = () => onDeclarationInadmissibility(bidId)
  const handlePurchaseOrderStatus =
    ({ id, code }) =>
    () => {
      openPurchaseOrderStatus({ id, code })
    }

  useEffect(() => {
    if (statusBidId === QUOTE_BID_STATUS_DICTIONARY.INADMISSIBILITY && checked) {
      setCheckBoxValue(false)
      selectedCheckbox({
        valueCheck: false,
        offerId: bidId,
        typeOfferSelected: typeOffer,
        totalAmount: amount,
        checked,
        byInadmissibility: true
      })
    }
  }, [statusBidId])

  useEffect(() => {
    if (isFailedSelected) {
      setCheckBoxValue(false)
    }
  }, [isFailedSelected])

  return (
    <Card enableBorder active={active} color={checkInadmissibility ? 'default' : 'blank'} margin="0 0 8px">
      <GridContainer alignItems="center" justifyContent="center" spacing={3}>
        {isBuyer && checkValidation && (
          <>
            <GridItem sm={1}>
              {selectedOffers ? (
                <CheckBox
                  checked={selectedOffers}
                  disabled={(!checked && disabledOffer()) || loading}
                  onChange={handleCheckBox}
                />
              ) : (
                <CheckBox
                  checked={checked}
                  disabled={(!checked && disabledOffer()) || loading}
                  onChange={handleCheckBox}
                />
              )}
            </GridItem>
            <Divider orientation="vertical" flexItem />
          </>
        )}

        <GridItem sm={checkValidation ? 2 : 3} xs={12}>
          <GridContainer>
            <GridItem xs={12}>
              <Typography color="black3" fontSize="12px" lineHeight="16px" variant="caption">
                <Label>{companySize}</Label>
              </Typography>
            </GridItem>
            <ProtectByFeatures rule={features => features['regions.tag']}>
              <GridItem xs={12}>
                <Typography color="black3" fontSize="12px" lineHeight="16px" variant="caption">
                  {regions}
                </Typography>
              </GridItem>
            </ProtectByFeatures>
            <GridItem xs={12}>
              <LetterBlue pointer href={providerFileUrl} target="_blank">
                {businessName}
              </LetterBlue>
            </GridItem>
            <GridItem xs={12}>
              <Typography color="black3" fontSize="14px" lineHeight="18px" variant="body2">
                {dni}
              </Typography>
            </GridItem>
          </GridContainer>
        </GridItem>
        <GridItem sm={5} xs={12}>
          <GridContainer>
            <GridItem xs={12}>
              <Typography color="black1" fontSize="14px" lineHeight="18px" margin="4px 0 8px" variant="body2">
                {description}
              </Typography>
            </GridItem>
          </GridContainer>
          <GridContainer>
            <GridItem xs={12}>
              <Typography color="gray1" fontSize="12px" fontWeight="bold" lineHeight="16px" variant="caption">
                VIGENCIA: {validityDate}
              </Typography>
            </GridItem>
          </GridContainer>
        </GridItem>
        <GridItem sm xs={12}>
          <GridContainer padding="0 16px 0 0">
            <GridItem xs={12}>
              <Typography
                align={size.isDownToTiny ? 'left' : 'right'}
                color="black1"
                fontSize="22px"
                fontWeight="bold"
                lineHeight="26px"
                variant="h3"
              >
                {amount && formatTypeCurrency(typeCurrency, amount.replace(/,/g, ''), true)}
              </Typography>
            </GridItem>
          </GridContainer>
          <GridContainer padding="0 16px 0 0">
            <GridItem xs={12}>
              <Typography
                align={size.isDownToTiny ? 'left' : 'right'}
                color="black3"
                fontSize="14px"
                lineHeight="18px"
                variant="body2"
              >
                Monto total
              </Typography>
            </GridItem>
          </GridContainer>
        </GridItem>
        <Divider flexItem orientation="vertical" />
        <GridItem container sm alignItems="center" xs={12}>
          <GridContainer>
            <GridItem xs={12}>
              <LetterBlue onClick={handleSeeDetail} pointer>
                Ver detalle
              </LetterBlue>
            </GridItem>
            <GridItem padding="4px 0 0" xs={12}>
              <LetterAbilityAffidavit onClick={handleAbilityAffidavit}>Declaración de habilidad</LetterAbilityAffidavit>
            </GridItem>

            <ProtectByFeatures rule={features => features['declaration.inadmissibility']}>
              <GridItem padding="4px 0 0" xs={12}>
                {statusBidId === QUOTE_BID_STATUS_DICTIONARY.INADMISSIBILITY && (
                  <GridItem lg={8} md={12} sm={12} xs={6}>
                    <Tag color="error" margin="0px" text="INADMISIBLE" />
                  </GridItem>
                )}
                {checkValidation &&
                  isBuyer &&
                  statusBidId !== QUOTE_BID_STATUS_DICTIONARY.INADMISSIBILITY &&
                  isSelectedBid && (
                    <LetterDeclarationInadmissibility onClick={handleDeclarationInadmissibility}>
                      Declarar inadmisible
                    </LetterDeclarationInadmissibility>
                  )}
              </GridItem>
            </ProtectByFeatures>
            {!isSelectedBid &&
              purchaseOrderStatus &&
              purchaseOrderStatus.map(
                po =>
                  po.isActive && (
                    <GridItem container alignItems="center" key={String(po.purchaseOrderId)} padding="4px 0 0" xs={12}>
                      <WorkIcon
                        color={checkPurchaseOrder(po.purchaseOrderEstatusCode) ? 'blue' : 'gray'}
                        margin="0 5px 0 0"
                      />
                      <LinkLabel
                        color={checkPurchaseOrder(po.purchaseOrderEstatusCode) ? 'blue' : 'gray'}
                        onClick={
                          checkPurchaseOrder(po.purchaseOrderEstatusCode)
                            ? handlePurchaseOrderStatus({ id: po.purchaseOrderId, code: po.purchaseOrderCode })
                            : undefined
                        }
                        pointer={checkPurchaseOrder(po.purchaseOrderEstatusCode)}
                      >
                        Orden de compra
                        {getPurchaseOrderStatusText(po.purchaseOrderEstatusCode)}
                      </LinkLabel>
                      {!checkPurchaseOrder(po.purchaseOrderEstatusCode) && (
                        <Tooltip
                          content={
                            <TooltipText>
                              El organismo público aún{' '}
                              <Strong>no envía la orden de compra al proveedor seleccionado</Strong>. Una vez enviada,
                              será visible desde esta opción.
                            </TooltipText>
                          }
                        >
                          <ExclamationIcon margin="0 0 0 5px" />
                        </Tooltip>
                      )}
                    </GridItem>
                  )
              )}
            {isSelectedBid &&
              purchaseOrderStatus &&
              purchaseOrderStatus.map(
                po =>
                  po.isActive && (
                    <GridItem container alignItems="center" key={String(po.purchaseOrderId)} padding="4px 0 0" xs={12}>
                      <WorkIcon
                        color={checkPurchaseOrder(po.purchaseOrderEstatusCode) ? 'blue' : 'gray'}
                        margin="0 5px 0 0"
                      />
                      <LinkLabel
                        color={checkPurchaseOrder(po.purchaseOrderEstatusCode) ? 'blue' : 'gray'}
                        onClick={
                          checkPurchaseOrder(po.purchaseOrderEstatusCode)
                            ? handlePurchaseOrderStatus({ id: po.purchaseOrderId, code: po.purchaseOrderCode })
                            : undefined
                        }
                        pointer={checkPurchaseOrder(po.purchaseOrderEstatusCode)}
                      >
                        Orden de compra
                        {getPurchaseOrderStatusText(po.purchaseOrderEstatusCode)}
                      </LinkLabel>
                      {!checkPurchaseOrder(po.purchaseOrderEstatusCode) && (
                        <Tooltip
                          content={
                            <TooltipText>
                              El organismo público aún{' '}
                              <Strong>no envía la orden de compra al proveedor seleccionado</Strong>. Una vez enviada,
                              será visible desde esta opción.
                            </TooltipText>
                          }
                        >
                          <ExclamationIcon margin="0 0 0 5px" />
                        </Tooltip>
                      )}
                    </GridItem>
                  )
              )}
          </GridContainer>
        </GridItem>
      </GridContainer>
      {inadmissibility && (
        <>
          <GridContainer>
            <GridItem xs={12} margin="25px auto 0 0">
              <Divider />
            </GridItem>
          </GridContainer>
          <GridContainer>
            <GridItem xs={12}>
              <Typography align="left" color="black1" fontSize="14px" lineHeight="18px" margin="20px auto 0">
                <Strong>Motivo de inadmisibilidad:</Strong> <span>{inadmissibility}</span>
              </Typography>
            </GridItem>
          </GridContainer>
        </>
      )}
    </Card>
  )
}

QuoteBidding.defaultProps = {
  active: false,
  bidId: '',
  businessName: '',
  companySize: '',
  dni: ''
}

QuoteBidding.propTypes = {
  active: PropTypes.bool,
  amount: PropTypes.string.isRequired,
  bidId: PropTypes.number,
  businessName: PropTypes.string,
  companySize: PropTypes.string,
  description: PropTypes.string.isRequired,
  disableOtherOffer: PropTypes.bool.isRequired,
  disableOtherOfferTotal: PropTypes.bool.isRequired,
  dni: PropTypes.string,
  inadmissibility: PropTypes.string,
  isFailedSelected: PropTypes.bool,
  isSelectedBid: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  onAbilityAffidavit: PropTypes.func.isRequired,
  onDeclarationInadmissibility: PropTypes.func.isRequired,
  onSeeDetail: PropTypes.func.isRequired,
  openPurchaseOrderStatus: PropTypes.func.isRequired,
  purchaseOrderStatus: PropTypes.arrayOf(
    PropTypes.shape({
      purchaseOrderId: PropTypes.number.isRequired,
      purchaseOrderEstatusCode: PropTypes.number.isRequired
    }).isRequired
  ).isRequired,
  regions: PropTypes.string,
  selectedCheckbox: PropTypes.func.isRequired,
  selectedOffers: PropTypes.string,
  statusBidId: PropTypes.number.isRequired,
  statusId: PropTypes.number.isRequired,
  typeCurrency: PropTypes.string.isRequired,
  typeOffer: PropTypes.string.isRequired,
  validityDate: PropTypes.string.isRequired
}

export default QuoteBidding
